import { useApolloClient } from '@apollo/client';
import { rgba } from 'polished';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import { useFeature, useSearch, useSessionSlots, useViewerRoles } from '@treams/treams-hooks';
import { Logo } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { FeatureName } from '@treams/treams-network';
import {
	ACCOUNT_MENU_POPOVER_PROPS,
	AccountMenu,
	AddIcon,
	Avatar,
	Button,
	ChevronDownIcon,
	ContextMenu,
	ConversationIcon,
	DialogueIcon,
	DraftsIcon,
	GearIcon,
	GeometryObserver,
	HeartIcon,
	Highlight,
	IconButton,
	Input,
	LibraryIcon,
	ListIcon,
	NoPrintWrapper,
	Paragraph,
	Portal,
	QuestionMarkIcon,
	SearchIcon,
	SendIcon,
	Skeleton,
	SplitButton,
	TargetIcon,
} from '@treams/treams-ui';

import { useManagerMode } from './ManagerModeContext';
import { TOP_BAR_HEIGHT } from './topBarHeight';

const Container = styled(Flex)`
	height: ${TOP_BAR_HEIGHT}px;
	padding: 11px ${(props) => props.theme.tokens.gap.element}px 11px 0;
	box-sizing: border-box;
	background: transparent;
`;

// Matches AppNav's pane padding so the logo and the menu button do not shift
// when the nav opens over the page.
const MobileContainer = styled(Flex)`
	height: ${TOP_BAR_HEIGHT}px;
	padding: 0 ${(props) => props.theme.tokens.padding.control}px;
	box-sizing: border-box;
`;

const Overlay = styled(Box)`
	position: absolute;
	top: 0px;
	left: 0;
	width: 100%;
	height: 100%;
	background: ${(props) => rgba(props.theme.tokens.text.primary, 0.24)};
	z-index: 2;
`;

const Results = styled(Box)`
	position: fixed;
	z-index: 1;
	box-sizing: border-box;
	background: ${(props) => props.theme.tokens.surface.white};
	border-radius: ${(props) => props.theme.tokens.radius.sm}px;
	box-shadow: ${(props) => props.theme.tokens.shadow.lg};
	border: 1px solid ${(props) => props.theme.tokens.border.default};
	padding: ${(props) => props.theme.tokens.padding.compact}px;
`;

const ResultLink = styled(Link)`
	display: block;
	color: ${(props) => props.theme.tokens.text.secondary};
	text-decoration: none;
`;

const Result = styled(Flex)<{ highlighted?: boolean }>`
	align-items: center;
	gap: ${(props) => props.theme.tokens.gap.element}px;
	padding: ${(props) => props.theme.tokens.padding.compact}px;
	border-radius: ${(props) => props.theme.tokens.radius.sm}px;
	font-size: ${(props) => props.theme.tokens.typography.sizes.label}px;
	font-weight: ${(props) => props.theme.tokens.typography.weights.medium};
	font-family: ${(props) => props.theme.tokens.typography.fontFamily};
	line-height: 20px;
	color: ${(props) => props.theme.tokens.text.secondary};

	${(props) => props.highlighted && `background: ${props.theme.tokens.surface.primary};`}
	&:hover {
		background: ${(props) => props.theme.tokens.surface.primary};
	}
`;

const LogoContainer = styled(Box)`
	transition: 0.4s ease-out opacity;

	svg {
		display: block;
		width: 140px;
		height: 50px;
	}

	img {
		display: block;
		max-width: 140px;
		max-height: 40px;
		object-fit: contain;
		object-position: left center;
	}

	&:hover {
		opacity: 0.75;

		&:hover {
			opacity: 0.5;
		}
	}
`;

const ProfileContainer = styled(Flex)`
	height: 33px;
	padding: 0 ${(props) => props.theme.tokens.padding.control}px;
	gap: ${(props) => props.theme.tokens.gap.element}px;
	border-radius: ${(props) => props.theme.tokens.radius.sm}px;
	align-items: center;
	flex-shrink: 0;
	min-width: 0;
	transition: 0.2s ease-out all;
	cursor: pointer;
	box-sizing: border-box;

	${Button.Styles.Secondary}

	svg {
		width: 16px;
		height: 16px;
		margin: 0;
		color: ${(props) => props.theme.tokens.icon.primary};
	}
`;

const KeyBinding = styled(Box)`
	box-sizing: border-box;
	height: 20px;
	display: inline-flex;
	align-items: center;
	gap: 4px;
	flex-grow: 0;
	flex-shrink: 0;
	border: 1px solid ${(props) => props.theme.tokens.border.default};
	background: ${(props) => props.theme.tokens.surface.white};
	padding: 0 6px;
	font-size: ${(props) => props.theme.tokens.typography.sizes.small}px;
	line-height: 1;
	border-radius: ${(props) => props.theme.tokens.radius.xs}px;
	color: ${(props) => props.theme.tokens.text.tertiary};
`;

const KeyBindingShortcut = styled.span`
	font-size: 14px;
	font-weight: bold;
	line-height: 1;
`;

export const Profile = Skeleton(
	function Profile({
		user,
		switcher,
		fullWidth,
		...boxProps
	}: BoxProps & { user: graphql.User; switcher?: boolean; fullWidth?: boolean }) {
		const client = useApolloClient();
		const history = useHistory();
		const { slots, switchSlot } = useSessionSlots();
		const { t } = useTranslation();
		const roles = useViewerRoles();
		const managermode = useManagerMode();
		const showRecommend = useFeature(FeatureName.ShowRecommendPage);

		// The switcher only lists the current workspace's accounts; other
		// workspaces are reached via their own subdomain.
		const hasMultipleAccounts = roles.isWorkspaceSuperuser || slots.length > 1;

		return (
			<ContextMenu.Target
				{...ACCOUNT_MENU_POPOVER_PROPS}
				style={{ display: 'block', width: fullWidth ? '100%' : undefined }}
				menu={
					<AccountMenu
						accounts={slots.map((account) => ({
							id: account.userID,
							user: account.user,
						}))}
						showAccounts={hasMultipleAccounts}
						showProfileActions={!switcher}
						showRecommend={showRecommend && roles.isWorkspaceSuperuser}
						addAccountLabel={t('menu.context.addAccount')}
						personalFilesLabel={t('menu.context.viewPersonalFiles')}
						settingsLabel={t('menu.context.personalSettings')}
						recommendLabel={t('menu.context.recommend')}
						logoutLabel={t('menu.context.logOut')}
						onSelectAccount={async (userID) => {
							const account = slots.find((slot) => slot.userID === userID);
							if (!account) {
								return;
							}

							// Only the Apollo cache needs clearing: the analytics IndexedDB is
							// keyed per user, so the incoming account opens its own database.
							await client.clearStore();

							try {
								await switchSlot(account.userID);
								managermode.disable();
							} catch {
								// The switch failed, so the old slot is still active and
								// expectedUserVar is unchanged; the resetStore below refetches
								// as the old user, instead of leaving the cleared store to
								// render as a full-page loader forever.
							} finally {
								await client.resetStore();
							}
						}}
						onAddAccount={() => {
							managermode.disable();
							history.push('/authentication?m');
						}}
						onPersonalFiles={() => history.push(`/users/${user.id}/personal-files`)}
						onSettings={() => history.push('/personal-settings')}
						onRecommend={() => history.push('/recommend')}
						onLogout={() => history.push('/logout')}
					/>
				}
			>
				<ProfileContainer {...boxProps} width={fullWidth ? '100%' : boxProps.width}>
					<Avatar user={user} size={16} />
					<Paragraph flex={1} minWidth={0} lineClamp={1}>
						{user.firstName} {user.lastName}
					</Paragraph>
					<ChevronDownIcon />
				</ProfileContainer>
			</ContextMenu.Target>
		);
	},
	function ProfileSkeleton(props: BoxProps) {
		return (
			<ProfileContainer {...props}>
				<Avatar.Skeleton size={16} />
				<Paragraph.Skeleton lines={1} width={120} />
			</ProfileContainer>
		);
	},
);

export function Search() {
	const { t } = useTranslation();

	const history = useHistory();

	const [query, setQuery] = useState('');
	const [focus, setFocus] = useState(false);
	const [index, setIndex] = useState<number | undefined>(undefined);
	const searchRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const now = useRef(Date.now()).current;

	const filters = [
		'isArchived:false',
		`activatedAt <= ${now}`,
		'(' + ['deactivatedAt = 0', `deactivatedAt > ${now}`].join(' OR ') + ')',
	].join(' AND ');

	const { results, loading } = useSearch(query, {
		filters,
		limit: 5,
		skip: query.length === 0,
		excludeViewer: true,
	});

	function reset() {
		setFocus(false);
		setQuery('');
		setIndex(undefined);
		(document.activeElement as HTMLElement)?.blur();
	}

	useEffect(() => {
		if (!loading) {
			setIndex(undefined);
		}
	}, [loading]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			const input = inputRef.current;
			const shortcut =
				event.key.toLowerCase() === 'k' &&
				(event.metaKey || (event.ctrlKey && !/Mac|iPhone|iPad|iPod/.test(navigator.platform)));

			if (!shortcut || !input || input.getClientRects().length === 0) {
				return;
			}

			event.preventDefault();
			input.focus();
		};

		document.addEventListener('keydown', onKeyDown);

		return () => document.removeEventListener('keydown', onKeyDown);
	}, []);

	const isRawLoading = loading ? loading : undefined;

	return (
		<>
			<div
				ref={searchRef}
				style={focus ? { position: 'relative', zIndex: 3, width: '100%' } : { width: '100%' }}
			>
				<Input
					search
					inputRef={inputRef}
					loading={isRawLoading}
					placeholder={t('menu.search.placeholder')}
					icon={<SearchIcon />}
					value={query}
					onKeyDown={(event) => {
						if (event.key === 'Escape') {
							reset();
						} else if (event.key === 'ArrowDown') {
							setIndex((index) => ((index ?? -1) + 1) % results.length);
							event.preventDefault();
						} else if (event.key === 'ArrowUp') {
							setIndex((index) => ((index ?? -1) - 1 + results.length) % results.length);
							event.preventDefault();
						} else if (event.key === 'Enter') {
							if (index !== undefined) {
								history.push(`/users/${results[index].id}`);
								reset();
							}
						}
					}}
					onFocus={() => setFocus(true)}
					onBlur={() => setFocus(false)}
					onChange={setQuery}
				/>
			</div>
			{focus && (
				<Portal>
					<Overlay>
						{results.length > 0 && (
							<GeometryObserver innerRef={searchRef}>
								{(bounds) => (
									<Results
										style={{
											top: bounds.top + bounds.height + 8,
											left: bounds.left,
											width: bounds.width,
										}}
									>
										{results.map((user, i) => (
											<ResultLink key={user.id} to={`/users/${user.id}`}>
												<Result
													onMouseEnter={() => setIndex(i)}
													onMouseDown={(event) => event.preventDefault()}
													onClick={reset}
													highlighted={index === i}
												>
													<Avatar user={user} size={20} />
													<Highlight query={query} flexGrow={1} flexShrink={1} minWidth={0}>
														{user.firstName} {user.lastName}
													</Highlight>
													{index === i && (
														<KeyBinding>
															Enter <KeyBindingShortcut>↵</KeyBindingShortcut>
														</KeyBinding>
													)}
												</Result>
											</ResultLink>
										))}
									</Results>
								)}
							</GeometryObserver>
						)}
					</Overlay>
				</Portal>
			)}
		</>
	);
}

function AddMenu() {
	const { t } = useTranslation();

	const hasCompliments = useFeature(graphql.FeatureName.Compliments);
	const hasGoals = useFeature(graphql.FeatureName.Goals);
	const hasFeedback = useFeature(graphql.FeatureName.Feedback);
	const canAskFeedback = useFeature(graphql.FeatureName.FeedbackAdhoc);
	const canGiveFeedback = useFeature(graphql.FeatureName.FeedbackSpontaneous);
	const hasConversations = useFeature(graphql.FeatureName.Evaluations);
	const hasNotes = useFeature(graphql.FeatureName.Notes);
	const hasLearning = useFeature(graphql.FeatureName.Learning);
	const isAdhocEnabled = useFeature(graphql.FeatureName.EvaluationsAdhoc);

	const items = [
		hasGoals
			? {
					title: t('menu.actions.createGoal'),
					icon: <TargetIcon size={20} />,
					link: '/goals/new',
				}
			: null,
		hasFeedback && canAskFeedback
			? {
					title: t('menu.actions.askFeedback'),
					icon: <DialogueIcon size={20} />,
					link: '/feedback/request-from-moment',
				}
			: null,
		hasFeedback && canGiveFeedback
			? {
					title: t('menu.actions.giveFeedback'),
					icon: <SendIcon size={20} />,
					link: '/feedback/send',
				}
			: null,
		hasCompliments
			? {
					title: t('menu.actions.giveCompliment'),
					icon: <HeartIcon size={20} />,
					link: '/feedback/compliments/new',
				}
			: null,
		hasConversations && isAdhocEnabled
			? {
					title: t('menu.actions.requestConversation'),
					icon: <ConversationIcon size={20} />,
					link: '/conversations/request-from-moment',
				}
			: null,
		hasNotes
			? {
					title: t('menu.actions.createNote'),
					icon: <DraftsIcon size={20} />,
					link: '/notes/new',
				}
			: null,
		hasLearning
			? {
					title: t('menu.actions.createLearningEvent'),
					icon: <LibraryIcon size={20} />,
					link: '/learning/events/new',
				}
			: null,
	].filter((item) => item !== null);

	const primary = items[0];
	if (!primary) {
		return null;
	}

	return (
		<SplitButton
			icon={<AddIcon size={16} />}
			title={primary.title}
			link={primary.link}
			items={items}
		/>
	);
}

export function AccountSwitcher() {
	const query = graphql.useViewerQuery();

	return query.data ? (
		<Profile user={query.data.viewer as graphql.User} switcher />
	) : (
		<Profile.Skeleton />
	);
}

const DesktopMenuContainer = styled(Box)`
	background: transparent;
	flex-shrink: 0;
`;

const Actions = styled(Flex)`
	align-items: center;
	gap: ${(props) => props.theme.tokens.gap.element}px;
`;

const SearchSlot = styled(Box)`
	flex-grow: 1;
	max-width: 480px;
	width: 100%;
`;

function DesktopMenu(props: { plain?: boolean }) {
	const query = graphql.useViewerQuery();

	return (
		<DesktopMenuContainer>
			<Container alignItems="center">
				{!props.plain && (
					<>
						<Actions>
							<AddMenu />
						</Actions>
						<Flex flexGrow={1} justifyContent="center" display={['none', 'none', 'flex']} px={3}>
							<SearchSlot>
								<Search />
							</SearchSlot>
						</Flex>
						<Actions>
							<a href="https://guides.ontreams.com/nl/" target="_blank" rel="noreferrer">
								<IconButton type="secondary" icon={<QuestionMarkIcon />} />
							</a>
							<IconButton type="secondary" icon={<GearIcon />} link="/personal-settings" />
							{query.data ? (
								<Profile user={query.data.viewer as graphql.User} />
							) : (
								<Profile.Skeleton />
							)}
						</Actions>
					</>
				)}
			</Container>
		</DesktopMenuContainer>
	);
}

function DesktopMenuSkeleton() {
	return (
		<DesktopMenuContainer>
			<Container alignItems="center">
				<Actions>
					<Button.Skeleton small />
				</Actions>
				<Flex flexGrow={1} justifyContent="center" display={['none', 'none', 'flex']} px={3}>
					<SearchSlot>
						<Input.Skeleton search />
					</SearchSlot>
				</Flex>
				<Actions>
					<IconButton type="secondary" icon={<QuestionMarkIcon />} />
					<IconButton type="secondary" icon={<GearIcon />} />
					<Profile.Skeleton />
				</Actions>
			</Container>
		</DesktopMenuContainer>
	);
}

function MobileMenu(props: { onOpen: () => void }) {
	const theme = useTheme();
	const { t } = useTranslation();

	return (
		<MobileContainer alignItems="center" justifyContent="space-between">
			{theme.images.logo ? (
				<Link to="/">
					<LogoContainer flexGrow={0} flexShrink={0}>
						{theme.images.logo.startsWith('/') ? (
							<Logo />
						) : (
							<img src={theme.images.logo} alt="logo" />
						)}
					</LogoContainer>
				</Link>
			) : (
				<Skeleton.Box width={140} height={50} />
			)}
			<IconButton
				type="secondary"
				icon={<ListIcon />}
				aria-label={t('sidebar.menu')}
				onClick={props.onOpen}
			/>
		</MobileContainer>
	);
}

export default function TopBar(props: { plain?: boolean; onOpen?: () => void }) {
	return (
		<NoPrintWrapper>
			<Box display={['none', 'none', 'block']}>
				<DesktopMenu plain={props.plain} />
			</Box>

			{props.onOpen && (
				<Box display={['block', 'block', 'none']}>
					<MobileMenu onOpen={props.onOpen} />
				</Box>
			)}
		</NoPrintWrapper>
	);
}

function TopBarSkeleton() {
	return (
		<NoPrintWrapper>
			<Box display={['none', 'none', 'block']}>
				<DesktopMenuSkeleton />
			</Box>
		</NoPrintWrapper>
	);
}

TopBar.Skeleton = TopBarSkeleton;
